import React, { ReactNode, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import { intl } from "LocaleWrapper";
import moment, { Moment } from "moment";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Card, PageHeader, Select, showSnackbar } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Box, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { StarRateRounded } from "@mui/icons-material";

import Helmet from "components/helmet/Helmet";
import Planboard from "components/planboard2/Planboard";
import useLayout from "components/layout/useLayout";
import ResizeHandle from "components/resizeContainer/ResizeHandle";

import AvailabilitySideviewer from "view/availability/components/sideviewer/AvailabilitySideviewer";
import AvailabilityCreateEditDialog from "view/availability/components/dialogs/AvailabilityCreateEditDialog";

import { Language } from "imports/Utils";

import { createAvailabilityTasks, createClosingHoursTasks, createQuayPlanningTasks } from "./utils";

import Cargos, { LocationPlanningUnplannedAction } from "./sections/Cargos";

import { ChildPropsFromArray, ChildPropsFromPagination } from "types/commons";

import { DateUtils, FormattedDateWindow } from "utils/DateUtils";

import "./styling/styling.scss";
import {
	LocationPlanningPlanCargoActionsOnQuayDocument,
	LocationPlanningUnplanQuayPlanningDocument,
	LocationPlanningUpdateQuayPlanningDocument,
} from "__generated__/view/locationplanning/graphql/locationplanning.mutations.generated";
import { ShipLinkDialog } from "view/charters/dossier/ship/ShipLinkDialog";
import {
	LocationPlanningPlanboardDocument,
	LocationPlanningPlanboardQuery,
	LocationPlanningSearchLocationsDocument,
	LocationPlanningSearchLocationsQuery,
} from "__generated__/view/locationplanning/graphql/locationplanning.queries.generated";
import { LocationAvailabilityType, LocationType } from "__generated__/enums";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CustomMarker } from "react-calendar-timeline";
import { useConfirm } from "components/hooks/useConfirm";
import DateRangePicker from "components/cck/DateRangePicker";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";
import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";

type Group = {
	id: string | number;
	title: ReactNode;
	droppable?: boolean;
	bestQuay?: boolean;
	quayId?: number;
	visible: boolean;
};

export type Item = {
	id: string | number;
	group: string | number;
	start: Moment;
	end: Moment;
	className?: string;
	title?: ReactNode;
	description?: string;
	entity?: QuayPlanning;
	canMove?: boolean;
	canResize?: string;
};

export type Window = {
	from: number | null;
	to: number | null;
};

type QuayPlanning = ChildPropsFromArray<LocationPlanningPlanboardQuery, "quayPlanningsForLocations">;
type LocationAvailability = ChildPropsFromArray<LocationPlanningPlanboardQuery, "locationAvailabilities">;
export type LocationFromQuery = ChildPropsFromPagination<LocationPlanningSearchLocationsQuery, "searchLocations">;

const LocationPlanning = () => {

	const [ layoutColumnSizes, setLayoutColumnSizes ] = useState(localStorage.getItem("locationPlanningLayoutColumnSizes") ? JSON.parse(localStorage.getItem("locationPlanningLayoutColumnSizes") as string) as [number, number] : [ 50, 50 ]);
	const [ availabilityDialogOpen, openAvailabilityDialog ] = useState(false);
	const [ availabilityUpdateRequestId, setAvailabilityUpdateRequestId ] = useState(0);
	const [ cargosRequestId, setCargosRequestId ] = useState(0);

	const [ scrollToDate, setScrollToDate ] = useState<number | undefined>(undefined);
	const [ dragging, setDragging ] = useState<{ enabled: boolean, cargoAction?: LocationPlanningUnplannedAction }>({
		enabled: false,
		cargoAction: undefined,
	});
	const [ window, setWindow ] = useState<Window>({
		from: moment().add(-14, "days").startOf("day").valueOf(),
		to: moment().add(30, "days").endOf("day").valueOf(),
	});

	const { data, loading } = useQuery(LocationPlanningSearchLocationsDocument, {
		variables: {
			pagination: {
				page: 0,
				size: 50,
			},
			locationSearch: {
				types: [ 16 ],
				isActive: true,
			},
		},
	});

	const onLayout = (sizes: number[]) => {
		setLayoutColumnSizes(sizes);
		localStorage.setItem("locationPlanningLayoutColumnSizes", JSON.stringify(sizes));
	};

	return (
		<div className="location-planning-page">
			<PageHeader>
				<PageHeader.Title>
					{Language("LOCATION_PLANNING")}
					<Helmet title={intl.formatMessage({ id: "LOCATION_PLANNING" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Box sx={{ width: "200px" }}>
						<DateRangePicker
							startDate={DateUtils.getDateOrUndefined(window.from)}
							endDate={DateUtils.getDateOrUndefined(window.to)}
							onChange={(window) => {
								setWindow({
									from: DateUtils.getTimestamp(window[0]),
									to: DateUtils.getTimestamp(window[1]),
								});
							}}
						/>
					</Box>
					<Button onClick={() => openAvailabilityDialog(true)} disabled={loading}>
						{intl.formatMessage({ id: "ADD_A_EXCEPTION_2" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			{loading ? (
				<CircularProgress size={24} />
			) : (
				<React.Fragment>
					<div className="resize-container">
						<PanelGroup direction="horizontal" onLayout={onLayout}>
							<Panel minSize={10} defaultSize={layoutColumnSizes[0]}>
								<div className="left">
									<div style={{ paddingRight: 24 }}>
										<Cargos
											onStartDragging={(cargoAction) => setDragging({ enabled: true, cargoAction: cargoAction })}
											onFinishDragging={() => setDragging({ enabled: false, cargoAction: undefined })}
											setScrollToDate={(date) => {
												setScrollToDate(date);
												setInterval(() => setScrollToDate(undefined), 100);
											}}
											window={window}
											cargosRequestId={cargosRequestId}
										/>
									</div>
								</div>
							</Panel>

							<ResizeHandle />

							<Panel minSize={10} defaultSize={layoutColumnSizes[1]}>
								<div style={{ paddingLeft: 24, maxHeight: "100%", overflowY: "auto" }}>
									<Board
										locations={data?.searchLocations.content || []}
										isDragging={dragging.enabled}
										draggedCargoAction={dragging.cargoAction}
										layoutColumnSizes={layoutColumnSizes}
										availabilityUpdateRequestId={availabilityUpdateRequestId}
										setCargosRequestId={() => setCargosRequestId(cargosRequestId + 1)}
										scrollToDate={scrollToDate}
										window={window}
									/>
								</div>
							</Panel>
						</PanelGroup>
					</div>

					<AvailabilityCreateEditDialog
						open={availabilityDialogOpen}
						locationTypes={[ LocationType.ExtractionMachine ]}
						onClose={() => openAvailabilityDialog(false)}
						refetch={() => setAvailabilityUpdateRequestId(availabilityUpdateRequestId + 1)}
						presetAvailabilityType={LocationAvailabilityType.CraneAvailability}
					/>
				</React.Fragment>
			)}
		</div>
	);
};

type BoardProps = {
	locations: LocationFromQuery[];
	isDragging: boolean;
	draggedCargoAction?: LocationPlanningUnplannedAction;
	layoutColumnSizes: number[];
	availabilityUpdateRequestId: number;
	setCargosRequestId: () => void;
	scrollToDate?: number;
	window: Window;
};

const Board = ({
	locations,
	isDragging,
	draggedCargoAction,
	layoutColumnSizes,
	availabilityUpdateRequestId,
	setCargosRequestId,
	scrollToDate,
	window,
}: BoardProps) => {

	const layout = useLayout();
	const confirm = useConfirm();

	const handleOnAvailabilityClick = (availability: LocationAvailability) => {
		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "QUAY_AVAILABILITY" }),
			content: (
				<Box p={2}>
					<AvailabilitySideviewer
						location={availability.location}
						availabilityId={availability.id}
						onUpdate={async () => {
							await refetch();
						}}
						onDelete={async () => {
							layout.closeDrawer();
							await refetch();
						}}
					/>
				</Box>
			),
			size: "normal",
			variant: "overlayWithMask",
		});
	};

	const openCallSideviewer = (quayPlanning: QuayPlanning) => {
		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "CALL" }),
			content: (
				<CallSideviewer
					defaultCallId={quayPlanning.cargoAction.call?.id ?? null}
					nextCallId={null}
					refetch={async () => {
						await refetch();
					}}
					bargeType={null}
					isShipEditable
				/>
			),
			size: "normal",
			variant: "overlayWithMask",
		});
	};

	const unplanActionFromQuay = (quayPlanning: QuayPlanning) => {
		confirm({
			type: "DELETE",
		}).then(async () => {
			await unplanQuayPlanning({ variables: { quayPlanningId: quayPlanning.id } });
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			setCargosRequestId();
			await refetch();
		});
	};

	const onToggleGroup = (groupId: number) => {
		if (collapsedGroups.includes(groupId)) {
			setCollapsedGroups(collapsedGroups => collapsedGroups.filter(it => it !== groupId));
		} else {
			setCollapsedGroups(collapsedGroups => [ ...collapsedGroups, groupId ]);
		}
	};

	const [ collapsedGroups, setCollapsedGroups ] = useState<number[]>([]);
	const [ selectedLocations, setSelectedLocations ] = useState<LocationSelectOption[] | null>(null);

	const [ editedQuayPlanning, setEditedQuayPlanning ] = useState<{
		default: QuayPlanning,
		new: { start: number, end: number, quayId: number },
	} | null>(null);
	const [ showAvailabilities, setShowAvailabilities ] = useState<boolean>(false);
	const [ planCargoActionDialogData, setPlanCargoActionDialogData ] = useState<{
		quayId: number;
		action: LocationPlanningUnplannedAction;
	} | null>(null);

	const enableLocationPlanningEditMode = (quayPlanning: QuayPlanning) => {
		setEditedQuayPlanning({
			default: quayPlanning,
			new: {
				start: quayPlanning.start,
				end: quayPlanning.end,
				quayId: quayPlanning.quay.id,
			},
		});
	};

	const { data, refetch, loading } = useQuery(LocationPlanningPlanboardDocument, {
		skip: locations.length < 1 || !window.from || !window.to,
		variables: {
			window: {
				from: window.from,
				to: window.to,
			},
			locationIds: locations.map(it => it.id),
		},
	});

	const availabilityItems = data ? createAvailabilityTasks(data.locationAvailabilities, handleOnAvailabilityClick) : [];
	const openingHoursItems = createClosingHoursTasks(locations, window);
	const quayPlanningItems = data ? createQuayPlanningTasks(
		data.quayPlanningsForLocations,
		editedQuayPlanning,
		enableLocationPlanningEditMode,
		openCallSideviewer,
		unplanActionFromQuay,
	) : [];

	const [ planCargoAction, { loading: planCargoActionLoading } ] = useMutation(LocationPlanningPlanCargoActionsOnQuayDocument);
	const [ updateLocationPlanning, { loading: updateLocationPlanningLoading } ] = useMutation(LocationPlanningUpdateQuayPlanningDocument);
	const [ unplanQuayPlanning, { loading: unplanQuayPlanningLoading } ] = useMutation(LocationPlanningUnplanQuayPlanningDocument);

	const onDropCargoAction = (group: Group, action: LocationPlanningUnplannedAction) => {

		const quayId = group.quayId;

		if (!quayId) {
			// This should never happen, and even if it does,
			// it is not the fault of the user; thus no language key is required
			showSnackbar("QuayId not provided, please contact your administrator and report this issue", { variant: "error" });
			console.error(`quayId must be a number, got "${quayId}" for group ${group}`);
			return;
		}

		if (action.call?.voyage) {
			planCargoAction({
				variables: {
					cargoActionId: action.id,
					quayId: quayId,
				},
			}).then(async (response) => {
				response.data?.planCargoActionOnQuay.predictionWarnings
					.forEach(warning => showSnackbar(intl.formatMessage({ id: warning }), { variant: "warning" }));
				setCargosRequestId();
				await refetch();
			});
		} else {
			setPlanCargoActionDialogData({ quayId, action });
		}
	};

	const onPlanChange = (quayPlanning: QuayPlanning, start: number, end: number, group: Group) => {
		if (editedQuayPlanning) {
			if (typeof group.id === "string") {
				const [ _location, locationId, _quay, quayId ] = group.id.split("_");

				if (+locationId === quayPlanning.cargoAction.location?.id) {
					// Replanning is allowed only within the same location

					const newStart = start > end ? end : start;
					const newEnd = end > start ? end : start;

					setEditedQuayPlanning({
						...editedQuayPlanning,
						new: {
							start: newStart,
							end: newEnd,
							quayId: +quayId,
						},
					});
					return;
				}
			}

			// This is needed because the planboard component keeps its own state,
			//  so we need to force it to return to the current state
			setEditedQuayPlanning({ ...editedQuayPlanning });
		}
	};

	usePropertyChangeEffect(async () => {
		await refetch();
	}, [ availabilityUpdateRequestId ]);

	return (
		<div className={isDragging ? "is-dragging" : undefined}>
			{editedQuayPlanning && (
				<ReplanPreview
					defaultQuayPlanning={editedQuayPlanning.default}
					newQuayPlanning={{
						...editedQuayPlanning.new,
						quay: locations.flatMap(location => location.quays)
							.find(quay => quay?.id === editedQuayPlanning.new.quayId),
					}}
					loading={updateLocationPlanningLoading}
					onCancel={() => {
						setEditedQuayPlanning(null);
					}}
					onSave={async () => {
						await updateLocationPlanning({
							variables: {
								input: {
									id: editedQuayPlanning.default.id,
									quayId: editedQuayPlanning.new.quayId,
									start: editedQuayPlanning.new.start,
									end: editedQuayPlanning.new.end,
								},
							},
						});

						setEditedQuayPlanning(null);
						await refetch();
						setCargosRequestId();
					}}
				/>
			)}

			<Card>
				{/*@ts-expect-error*/}
				<Planboard
					id={"locationPlanningPlanboard"}
					planboardKey={layoutColumnSizes[1]}
					loading={loading || unplanQuayPlanningLoading}
					scrollToDate={scrollToDate}
					groups={locations.flatMap((location): Group[] => [
						{
							id: location.id,
							title: (
								<div className="sidebar-sub-toggle" onClick={() => onToggleGroup(location.id)}>
									{collapsedGroups.includes(location.id) ? (
										<KeyboardArrowRightIcon />
									) : (
										<KeyboardArrowDownIcon />
									)}
									{location.description}
								</div>
							),
							visible: selectedLocations ? selectedLocations.some(l => l.id === location.id) : true,
						},
						...(!collapsedGroups.includes(location.id) && location.quays ? location.quays.map(quay => ({
							id: `location_${location.id}_quay_${quay.id}`,
							title: (
								<div className="sidebar-sub-group">
									{quay.name1}
								</div>
							),
							quayId: quay.id,
							droppable: location.id === draggedCargoAction?.location?.id,
							visible: selectedLocations ? selectedLocations.some(l => l.id === location.id) : true,
							bestQuay: quay.id === draggedCargoAction?.processingTime?.quay?.id,
						})) : []),
					]).filter(group => group.visible)}
					items={[
						...openingHoursItems,
						...availabilityItems,
						...quayPlanningItems,
					]}
					selected={editedQuayPlanning ? [ `quayPlanning-${editedQuayPlanning.default.id}` ] : undefined}
					stackItems={false}
					onItemMove={onPlanChange}
					onItemResize={onPlanChange}
					markers={draggedCargoAction ? [
						draggedCargoAction.processingTime?.window.from ? (
							<CustomMarker date={draggedCargoAction.processingTime.window.from}>
								{({ styles }: { styles: React.CSSProperties }) => {
									return <div style={{ ...styles, backgroundColor: "green", width: "2px" }} />;
								}}
							</CustomMarker>
						) : null,
						draggedCargoAction.processingTime?.window.to ? (
							<CustomMarker date={draggedCargoAction.processingTime.window.to}>
								{({ styles }: { styles: React.CSSProperties }) => {
									return <div style={{ ...styles, backgroundColor: "green", width: "2px" }} />;
								}}
							</CustomMarker>
						) : null,
					].filter(Boolean) : []}
					dragSnap={15 * 60 * 1000}
					groupRenderer={({ group }: { group: Group }) => {
						if (group.droppable) {
							return (
								<div
									className="droppable"
									onDrop={(event) => {
										const cargoAction = JSON.parse(event.dataTransfer.getData("cck-table"));
										(event.target as HTMLElement).style.background = "inherit";
										onDropCargoAction(group, cargoAction);
									}}
									onDragOver={(event) => {
										event.preventDefault();
									}}
									onDragEnter={(event) => {
										event.preventDefault();
										const target = event.target as HTMLElement;
										target.style.background = "#86c288";
										target.style.borderRadius = "inherit";
									}}
									onDragLeave={(event) => {
										event.preventDefault();
										(event.target as HTMLElement).removeAttribute("style");
									}}>
									{group.bestQuay && <span className="icon"><StarRateRounded color="success" sx={{ fontSize: 16 }} /></span>}
									{group.title}
								</div>
							);
						} else {
							if (isDragging) {
								return <span style={{ opacity: 0.25 }}>{group.title}</span>;
							}
							return group.title;
						}
					}}
					//@ts-expect-error
					itemRenderer={({ item, itemContext, getItemProps, getResizeProps }) => {

						const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();

						const itemProps = { ...getItemProps(item.itemProps) };
						const disabled = (editedQuayPlanning && item.entity?.id !== editedQuayPlanning.default.id);

						return (
							<div
								{...getItemProps(item.itemProps)}
								title={item.description ?? itemContext.title}
								className={`${itemProps.className} ${showAvailabilities ? "show-availabilities" : ""} ${disabled ? "disabled" : ""}`}
							>
								{itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ""}

								<div className="rct-item-content" style={{ maxHeight: `${itemContext.dimensions.height}` }}>
									{editedQuayPlanning ? <React.Fragment /> : itemContext.title}
								</div>

								{itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ""}
							</div>
						);
					}}
					onRefresh={async () => {
						await refetch();
					}}
					optionalTools={(
						<Box sx={{ display: "flex" }} gap={4}>
							<Box style={{ width: "200px" }}>
								<Select
									fetchOptions={locations}
									getOptionLabel={option => option.description}
									value={selectedLocations}
									onChange={options => setSelectedLocations(options)}
									multiple
									placeholder={!selectedLocations ? intl.formatMessage({ id: "SELECT_LOCATIONS" }) : undefined}
								/>
							</Box>

							<FormControlLabel
								control={(
									<Switch
										checked={showAvailabilities}
										onChange={(event) => setShowAvailabilities(event.target.checked)}
										size="small"
									/>
								)}
								label={(
									<span style={{ fontSize: 13, fontWeight: 500 }}>
										{intl.formatMessage({ id: "SHOW_AVAILABILITIES" })}
									</span>
								)}
							/>
						</Box>
					)}
				/>
			</Card>

			{planCargoActionDialogData?.quayId && planCargoActionDialogData.action?.cargo.charter && (
				<ShipLinkDialog
					open={true}
					resetShipNomination
					onClose={() => setPlanCargoActionDialogData(null)}
					charter={planCargoActionDialogData.action.cargo.charter}
					overrideSaveMutation={{
						loading: planCargoActionLoading,
						overrideFunction: async (planVoyageInput) => {
							const result = await planCargoAction({
								variables: {
									cargoActionId: planCargoActionDialogData.action.id,
									quayId: planCargoActionDialogData.quayId,
									planVoyageInput: planVoyageInput,
								},
							});

							return result.data?.planCargoActionOnQuay;
						},
					}}
					onCompleted={async (result) => {
						showSnackbar(
							intl.formatMessage({ id: "ACTION_PLANNED_ON_VOYAGE" }) + " " + result.voyage?.voyageNumber,
							{ variant: "success" },
						);
						for (const predictionWarning of result.predictionWarnings) {
							showSnackbar(intl.formatMessage({ id: predictionWarning }), { variant: "warning" });
						}

						setCargosRequestId();
						await refetch();
					}}
				/>
			)}
		</div>
	);
};

const ReplanPreview = ({
	defaultQuayPlanning,
	newQuayPlanning,
	loading,
	onCancel,
	onSave,
}: {
	defaultQuayPlanning: QuayPlanning,
	newQuayPlanning: { start: number, end: number, quay: QuayPlanning["quay"] | null | undefined },
	loading: boolean;
	onCancel: () => void,
	onSave: () => void,
}) => {
	return (
		<div className="replan-preview">
			<Card
				title={(
					<>
						{defaultQuayPlanning.cargoAction.cargo.product?.name}
						{" - "}
						{defaultQuayPlanning.cargoAction.location?.description}
					</>
				)}
				actions={[ {
					icon: <HighlightOffIcon />,
					onClick: () => onCancel(),
				}, {
					icon: <SaveIcon />,
					onClick: () => onSave(),
					disabled: loading,
				} ]}>
				{loading ? (
					<CircularProgress size={16} />
				) : (
					<React.Fragment>
						<p>
							{intl.formatMessage({ id: "CARGO_ACTION_START_END" })}:

							{" "}

							<span className="line-through">
								<FormattedDateWindow
									from={defaultQuayPlanning.start}
									to={defaultQuayPlanning.end}
									withoutYear
								/>
							</span>

							{" | "}

							<FormattedDateWindow
								from={newQuayPlanning.start}
								to={newQuayPlanning.end}
								withoutYear
							/>
						</p>

						<p>
							{intl.formatMessage({ id: "QUAY" })}:
							{" "}
							{newQuayPlanning.quay ? (
								<>
									<span className="line-through">{defaultQuayPlanning.quay.name1}</span>
									{` | ${newQuayPlanning.quay.name1}`}
								</>
							) : (
								<span>{defaultQuayPlanning.quay.name1}</span>
							)}
						</p>
					</React.Fragment>
				)}
			</Card>
		</div>
	);
};

export default LocationPlanning;
